// src/components/RegisterModal.js
import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { usersAPI } from '../services/api';

const PROFESSIONS = [
  { key:'advocate',  label:'⚖️ Advocate' },
  { key:'judge',     label:'🏛 Judge' },
  { key:'student',   label:'📚 Student' },
  { key:'academic',  label:'🎓 Academic' },
  { key:'corporate', label:'🏢 Corporate' },
  { key:'other',     label:'👤 Other' },
];

const RegisterModal = ({ onComplete }) => {
  const { getToken, setCurrentUser, currentUser } = useApp();
  const [name,         setName]         = useState(currentUser?.name || '');
  const [phone,        setPhone]        = useState(currentUser?.phone || '');
  const [profession,   setProfession]   = useState('');
  const [barCouncil,   setBarCouncil]   = useState('');
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState('');

  const handleSave = async () => {
    if (!name.trim())       return setError('Full name is required');
    if (!profession)        return setError('Please select your profession');
    setError(''); setSaving(true);
    try {
      const token = await getToken();
      await usersAPI.updateMe(token, {
        name:       name.trim(),
        phone:      phone.trim(),
        profession,
        barCouncilNo: barCouncil.trim(),
        profileComplete: true,
      });
      setCurrentUser(prev => ({
        ...prev,
        name: name.trim(),
        phone: phone.trim(),
        profession,
        barCouncilNo: barCouncil.trim(),
        profileComplete: true,
        initials: name.trim().split(' ').map(n => n[0]).join('').toUpperCase().substring(0,2),
      }));
      onComplete();
    } catch (e) {
      setError('Failed to save. Please try again.');
    } finally { setSaving(false); }
  };

  return (
    <div style={{
      position:'fixed', inset:0, zIndex:99999,
      background:'rgba(44,44,53,.6)', backdropFilter:'blur(4px)',
      display:'flex', alignItems:'center', justifyContent:'center', padding:16,
    }}>
      <div style={{
        background:'white', borderRadius:20, padding:28,
        maxWidth:400, width:'100%', boxShadow:'0 8px 40px rgba(0,0,0,.2)',
        maxHeight:'90vh', overflowY:'auto',
      }}>
        {/* Header */}
        <div style={{ textAlign:'center', marginBottom:20 }}>
          <div style={{ fontSize:36, marginBottom:8 }}>👋</div>
          <div style={{ fontFamily:'Playfair Display,serif', fontSize:20, fontWeight:700, color:'var(--ink)', marginBottom:4 }}>
            Welcome to AIR Reader!
          </div>
          <div style={{ fontSize:12, color:'var(--ink-lt)', fontFamily:'Nunito,sans-serif', lineHeight:1.5 }}>
            Complete your profile to get started
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{ background:'var(--rose-pale)', border:'1px solid var(--rose-lt)', borderRadius:8,
            padding:'8px 12px', fontSize:12, color:'var(--rose)', fontFamily:'Nunito,sans-serif',
            marginBottom:14, lineHeight:1.5 }}>
            {error}
          </div>
        )}

        {/* Full Name */}
        <div style={{ marginBottom:14 }}>
          <label style={{ display:'block', fontSize:11, fontWeight:700, color:'var(--ink-mid)',
            fontFamily:'Nunito,sans-serif', marginBottom:6, textTransform:'uppercase', letterSpacing:1 }}>
            Full Name *
          </label>
          <input type="text" placeholder="e.g. Adv. Ramesh Kumar" value={name}
            onChange={e => { setName(e.target.value); setError(''); }} autoFocus
            style={{ width:'100%', padding:'11px 14px', borderRadius:10, fontSize:13,
              border:'1px solid var(--border)', background:'var(--off-white)',
              color:'var(--ink)', fontFamily:'Nunito,sans-serif', outline:'none',
              boxSizing:'border-box' }}/>
        </div>

        {/* Mobile */}
        <div style={{ marginBottom:14 }}>
          <label style={{ display:'block', fontSize:11, fontWeight:700, color:'var(--ink-mid)',
            fontFamily:'Nunito,sans-serif', marginBottom:6, textTransform:'uppercase', letterSpacing:1 }}>
            Mobile Number
          </label>
          <div style={{ display:'flex', gap:8 }}>
            <div style={{ padding:'11px 12px', borderRadius:10, border:'1px solid var(--border)',
              background:'var(--off-white)', fontSize:13, color:'var(--ink-mid)',
              fontFamily:'Nunito,sans-serif', whiteSpace:'nowrap' }}>
              🇮🇳 +91
            </div>
            <input type="tel" placeholder="10-digit number" value={phone}
              onChange={e => setPhone(e.target.value.replace(/\D/g,''))} maxLength={10}
              style={{ flex:1, padding:'11px 14px', borderRadius:10, fontSize:13,
                border:'1px solid var(--border)', background:'var(--off-white)',
                color:'var(--ink)', fontFamily:'Nunito,sans-serif', outline:'none' }}/>
          </div>
        </div>

        {/* Profession */}
        <div style={{ marginBottom:14 }}>
          <label style={{ display:'block', fontSize:11, fontWeight:700, color:'var(--ink-mid)',
            fontFamily:'Nunito,sans-serif', marginBottom:8, textTransform:'uppercase', letterSpacing:1 }}>
            I am a... *
          </label>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
            {PROFESSIONS.map(p => (
              <button key={p.key} onClick={() => { setProfession(p.key); setError(''); }}
                style={{
                  padding:'9px 12px', borderRadius:10, fontSize:12, fontWeight:700,
                  fontFamily:'Nunito,sans-serif', cursor:'pointer', textAlign:'left',
                  border: profession === p.key ? '2px solid var(--rose)' : '1px solid var(--border)',
                  background: profession === p.key ? 'var(--rose-pale)' : 'white',
                  color: profession === p.key ? 'var(--rose)' : 'var(--ink-mid)',
                  transition:'all .15s',
                }}>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Bar Council No — only for advocates */}
        {profession === 'advocate' && (
          <div style={{ marginBottom:14 }}>
            <label style={{ display:'block', fontSize:11, fontWeight:700, color:'var(--ink-mid)',
              fontFamily:'Nunito,sans-serif', marginBottom:6, textTransform:'uppercase', letterSpacing:1 }}>
              Bar Council Enrollment No. <span style={{ color:'var(--ink-faint)', fontWeight:400 }}>(optional)</span>
            </label>
            <input type="text" placeholder="e.g. MH/1234/2020" value={barCouncil}
              onChange={e => setBarCouncil(e.target.value)}
              style={{ width:'100%', padding:'11px 14px', borderRadius:10, fontSize:13,
                border:'1px solid var(--border)', background:'var(--off-white)',
                color:'var(--ink)', fontFamily:'Nunito,sans-serif', outline:'none',
                boxSizing:'border-box' }}/>
          </div>
        )}

        {/* Save Button */}
        <button onClick={handleSave} disabled={saving}
          style={{
            width:'100%', padding:'13px 0', background: saving ? 'var(--border)' : 'var(--rose)',
            color:'white', border:'none', borderRadius:12, fontSize:14, fontWeight:700,
            fontFamily:'Nunito,sans-serif', cursor: saving ? 'not-allowed' : 'pointer',
            display:'flex', alignItems:'center', justifyContent:'center', gap:8, marginTop:4,
          }}>
          {saving ? (
            <>
              <div style={{ width:16, height:16, border:'2px solid rgba(255,255,255,.3)',
                borderTopColor:'white', borderRadius:'50%', animation:'spin .8s linear infinite' }}/>
              <span>Saving...</span>
            </>
          ) : 'Complete Setup →'}
        </button>

        <p style={{ textAlign:'center', fontSize:10, color:'var(--ink-faint)',
          fontFamily:'Nunito,sans-serif', marginTop:12, lineHeight:1.5 }}>
          Your data is stored securely · DPDP Act 2023 compliant
        </p>
        <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
      </div>
    </div>
  );
};

export default RegisterModal;